/**
 * The next article or tag number, and the empty records those numbers are
 * written into.
 *
 * Kept as pure functions in their own file because the whole correctness of
 * the numbering lives here and nothing about it needs React or Firestore — the
 * actions in `components/NewArticleAction.tsx` and `components/NewTagAction.tsx`
 * are only the plumbing that fetches the ids, calls this, and navigates.
 *
 * **One numbering function, two collections, two independent sequences.**
 * `nextNumericSlug` takes the candidates it is given, so news and tags each
 * count from their own collection — article 3 and tag 3 are unrelated, which is
 * fine because they never share a URL namespace (`/news/3` vs `/news/tag/3`).
 */
import type { EntityReference } from "@firecms/core";
import type { Article, Tag } from "@one-ai/shared/cms/schema";

/** Only a whole decimal number counts as a numbered slug. */
const NUMERIC = /^\d+$/;

/**
 * `1` for an empty collection, otherwise one past the highest number in use.
 *
 * **Highest-in-use plus one, not a stored counter.** The trade was made
 * deliberately (2026-09-07): no counter document means no new
 * `firestore.rules` clause to deploy and nothing that can drift out of sync
 * with the collection, at the price of two consequences worth stating plainly.
 * Deleting the newest article frees its number for reuse — so a URL can be
 * recycled onto different content, which for an indexed page is worse than a
 * gap in the sequence. And two editors creating an article in the same few
 * seconds both compute the same number, and the second write overwrites the
 * first. Neither is likely at this collection's size; both are cured by a
 * counter document in a transaction if it ever bites.
 *
 * **Non-numeric ids are ignored, not rejected.** Every article created before
 * this landed has a hand-written slug (`new-service-launch`), and those keep
 * their URLs forever — Firestore cannot rename a document, and an indexed URL
 * that moves is a 404 with no server to redirect it. So they simply do not
 * participate in the sequence, which is why numbering starts at 1 even in a
 * collection that already holds articles.
 *
 * Takes every candidate string rather than only ids so the caller can pass a
 * document's `slug` field too: the two agree for anything this action creates,
 * but a record written directly in the Firebase console could carry a numeric
 * slug under a random id, and handing out that number again would collide on
 * the public URL rather than in Firestore. A tag stores no slug field at all —
 * its id is the only spelling — so `NewTagAction` passes ids alone.
 */
export function nextNumericSlug(candidates: Iterable<string>): string {
    let highest = 0;
    for (const candidate of candidates) {
        if (!NUMERIC.test(candidate)) continue;
        const value = Number(candidate);
        // `Number("999...")` past 2^53 loses precision and would keep handing
        // out the same number. Not reachable with hand-created content; cheap
        // to exclude rather than reason about.
        if (!Number.isSafeInteger(value)) continue;
        if (value > highest) highest = value;
    }
    return String(highest + 1);
}

/**
 * The FireCMS-side shape of a news article — `Article`, except for the two
 * fields the editor and the parsed record deliberately disagree about. Declared
 * here rather than imported from `collections/news.ts` so that file can import
 * the action without a cycle; `collections/news.ts` types its collection
 * against this one.
 */
export type ArticleCms = Omit<Article, "updatedAt" | "tags"> & {
    updatedAt: Date;
    tags: EntityReference[];
};

/**
 * The record the "New article" action writes before opening the form.
 *
 * **It must satisfy `ArticleSchema`, not merely be a plausible blank.**
 * `apps/one-ai`'s read path drops a document that fails `safeParse`, and
 * `assertParsedSomething` turns "every document dropped" into a red build — so
 * a draft missing `updatedAt` or `published` could fail a *production* build
 * over content nobody has finished writing yet. Empty strings parse fine
 * (`z.string()` has no `.min(1)`), which is what makes an empty-but-valid
 * draft possible at all.
 *
 * `published: false` is what keeps it off the live site. It does still build on
 * develop, where `NEXT_PUBLIC_BUILD_DRAFTS` is on — an untitled page at
 * `/news/<n>` there is the visible cost of creating a draft and walking away,
 * and deleting the record removes it.
 *
 * `description` is absent rather than blank: it is optional as of 2026-09-07,
 * and an absent key is the one blank shape that needs no tolerating.
 */
export function draftArticleValues({
    slug,
    date,
    now = new Date(),
}: {
    slug: string;
    /** Today, ISO date-only — `collections/today.ts` supplies it. */
    date: string;
    now?: Date;
}): ArticleCms {
    return {
        date,
        // FireCMS's `autoValue: "on_update"` only fires on a form save, and
        // this write does not go through the form.
        updatedAt: now,
        slug,
        published: false,
        tags: [],
        title: { zh: "", jp: "" },
        body: { zh: "", jp: "" },
    };
}

/**
 * The record the "New tag" action writes before opening the form.
 *
 * The tag half of the same trade as `draftArticleValues`: FireCMS cannot
 * pre-fill a custom document id, so the number can only be assigned by writing
 * the document first. Same obligation too — it must satisfy `TagSchema`, not
 * merely be a plausible blank, because `apps/one-ai` drops a document that
 * fails `safeParse` and `assertParsedSomething` turns "every document dropped"
 * into a red build.
 *
 * **`retired: true` is the analogue of `published: false`, and it is the whole
 * reason an empty tag is harmless.** An active tag with two blank labels would
 * render an empty chip in the filter bar and prerender `/news/tag/<n>` with an
 * empty title; retired, it renders nowhere at all until an editor fills the
 * labels in and flips the switch. `label` is `required` on both locales in the
 * collection, so the form will not let that first save through empty.
 *
 * `order` is absent rather than blank — the same call as `description` on an
 * article draft, and `visibleTags` already means "alphabetical" by it.
 *
 * `category: false` is written rather than left to the schema's default: this
 * document is created outside the form, and a draft must not be a filter-bar
 * category before an editor has decided it is one.
 */
export function draftTagValues({
    date,
}: {
    /** Today, ISO date-only — `collections/today.ts` supplies it. */
    date: string;
}): Tag {
    return {
        date,
        retired: true,
        category: false,
        label: { zh: "", jp: "" },
    };
}
